import { Box, BoxProps, Text, useToken } from "@chakra-ui/react";
import { GeoJsonGeometryTypes } from "geojson";
import { DataDrivenPropertyValueSpecification } from "maplibre-gl";
import { useContext } from "react";

import { Point } from "@nens/rana/api-v1";
import { RanaAlert } from "@nens/ranalib";
import { NETHERLANDS_EXTENT } from "@nens/shared-utils";

import { VisibleLayer } from "@/components/layer-control";
import { VECTOR_LAYER_ID } from "@/config/map-layer-constants";
import { AFTER_LABEL_LAYER_ID, FIT_BOUNDS_OPTIONS, RanaMap } from "@/lib/rana-map";
import {
  RanaMapPmTileLayerProps,
  getPmTileLayerType,
} from "@/lib/rana-map/components/rana-map-layers";
import { ProcessRunFormMapViewStateContext } from "@/stores/process-run-form-map-view-state-provider";
import { ProjectContext } from "@/stores/project-provider";

import { useSelectFeature } from "../hooks/use-select-feature";
import { FeatureSelectorFieldProps } from "../types/feature-selector-field-props-type";
import { FEATURE_LABEL_EXPRESSION } from "../utils/feature-label-expression";
import { featureSelectorGetMapId } from "../utils/feature-selector-get-map-id";

type Layer = {
  layer: string;
  url: string;
  type: GeoJsonGeometryTypes | undefined;
};

type ProcessRunFormFileFeatureSelectorMapProps = {
  children: React.ReactNode;
  isLoading: boolean;
  layerNames: Layer["layer"][];
  layers: Layer[];
  bounds: [number, number, number, number] | undefined | null;
  fieldProps: FeatureSelectorFieldProps;
  visibleLayers?: VisibleLayer[];
  // When true: Ignore file bounds and only zoom to project location (for standard boundary files).
  // When false/undefined: Zoom to project location initially, then fit to file bounds when uploaded (for user files).
  preferProjectLocation?: boolean;
  mapSuffix: string;
};

export const MAP_WRAPPER_PROPS: BoxProps = { position: "relative", h: "md" };

const getLayerProps = ({
  layer,
  type,
  color,
}: {
  layer: string;
  type: GeoJsonGeometryTypes | undefined;
  color: DataDrivenPropertyValueSpecification<string>;
}): Partial<RanaMapPmTileLayerProps> => {
  const pmTileLayerType = getPmTileLayerType(type);
  switch (pmTileLayerType) {
    case "circle":
      return {
        circleProps: {
          id: layer,
          paint: { "circle-color": color },
        },
      };
    case "line":
      return {
        lineProps: {
          id: layer,
          paint: { "line-color": color },
        },
      };

    case "fill":
    default:
      return {
        fillProps: {
          id: layer,
          paint: { "fill-color": color },
        },
      };
  }
};

// Create initial viewport state:
// - If fitting to bounds: Use bounds-based viewport
// - Otherwise: Use project location if available, fall back to Netherlands extent
const getInitialViewState = (
  shouldFitToBounds: boolean,
  bounds: [number, number, number, number] | undefined | null,
  projectLocation: Point | undefined | null
) => {
  if (shouldFitToBounds) {
    return {
      bounds: bounds ?? undefined,
      fitBoundsOptions: FIT_BOUNDS_OPTIONS,
    };
  }

  if (projectLocation) {
    return {
      longitude: projectLocation.coordinates[0],
      latitude: projectLocation.coordinates[1],
      zoom: 11,
    };
  }

  // Fallback to Netherlands extent if no project location available
  return {
    bounds: NETHERLANDS_EXTENT,
    fitBoundsOptions: FIT_BOUNDS_OPTIONS,
  };
};

export const ProcessRunFormFileFeatureSelectorMap = ({
  isLoading,
  bounds,
  layers,
  layerNames,
  visibleLayers,
  children,
  fieldProps,
  preferProjectLocation,
  mapSuffix,
}: ProcessRunFormFileFeatureSelectorMapProps) => {
  const project = useContext(ProjectContext);
  const { formData, readonly, disabled } = fieldProps;
  const isDisabled = readonly || disabled;
  const { onClick, areaError } = useSelectFeature(fieldProps, layerNames, mapSuffix);
  const [defaultColor, selectedColor, errorColor] = useToken("colors", [
    "map.default",
    "map.selected",
    "semantic.error.main",
  ]);
  const { mapViewState, setMapView } = useContext(ProcessRunFormMapViewStateContext);

  // Determine map zoom behavior:
  // - Standard files (preferProjectLocation=true): Show project location, never fit to file bounds
  // - Own files (preferProjectLocation=false/undefined): Show project location initially, fit to bounds when file uploaded
  const shouldFitToBounds = !preferProjectLocation && !!bounds;

  const initialViewState =
    mapViewState ?? getInitialViewState(shouldFitToBounds, bounds, project.location);

  return (
    <>
      <Box {...MAP_WRAPPER_PROPS}>
        <RanaMap
          mapId={featureSelectorGetMapId(fieldProps, mapSuffix)}
          isLoading={isLoading}
          bounds={shouldFitToBounds ? bounds : null}
          mapProps={{
            onClick: isDisabled ? undefined : onClick,
            onMoveEnd: setMapView,
            ...(shouldFitToBounds ? {} : { initialViewState }),
          }}
          deckGlProps={{}}
        >
          <RanaMap.MapScale />

          {children}

          {/* Vector layer */}
          {layers.map(({ layer, url, type }) => {
            if (!type) return null;
            const color: DataDrivenPropertyValueSpecification<string> =
              formData?.feature_ids && formData.layer_name === layer
                ? [
                    "case",
                    ["in", ["id"], ["literal", formData.feature_ids]],
                    areaError ? errorColor : selectedColor,
                    defaultColor,
                  ]
                : defaultColor;

            const isVisible = visibleLayers
              ? visibleLayers.some((visibleLayer) => visibleLayer.layer === layer)
              : true;

            return (
              <RanaMap.Source
                bounds={bounds}
                id={layer}
                key={layer}
                type="vector"
                url={`pmtiles://${url}`}
              >
                <RanaMap.PmTileLayer
                  source-layer={VECTOR_LAYER_ID}
                  type={type}
                  isVisible={isVisible}
                  {...getLayerProps({ layer, type, color })}
                />
                {/* Feature name labels for selected features */}
                <RanaMap.Layer
                  // Add after label layer id so the text-allow-overlap prioretizes this layer
                  beforeId={AFTER_LABEL_LAYER_ID}
                  id={`${layer}-labels`}
                  type="symbol"
                  source-layer={VECTOR_LAYER_ID}
                  filter={["in", ["id"], ["literal", formData?.feature_ids ?? []]]}
                  paint={{
                    "text-color": "#000000",
                    "text-halo-blur": 1,
                    "text-halo-color": "#ffffff",
                    "text-halo-width": 1,
                  }}
                  layout={{
                    "text-field": FEATURE_LABEL_EXPRESSION,
                    "text-font": ["Roboto"],
                    "text-size": 14,
                    "text-anchor": "center",
                    "text-allow-overlap": false,
                    "text-ignore-placement": false,
                  }}
                  isVisible={isVisible}
                />
              </RanaMap.Source>
            );
          })}
        </RanaMap>
      </Box>
      {areaError && (
        <RanaAlert fontSize="sm" iconSize="6" mt={4} status="error">
          <Text>{areaError}</Text>
        </RanaAlert>
      )}
    </>
  );
};
